import { useAuth0 } from '@auth0/auth0-react';
import { Menu, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import ThemeToggle from '../common/ThemeToggle';
import logoSvg from '../../assets/logo.svg';

interface TopBarProps {
  onMenuClick: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * The console's top bar. It spans the full width and the sidebar starts below it, which is how the
 * design lays the chrome out — hence the logo and the collapse control living here rather than in
 * the sidebar.
 */
export default function TopBar({ onMenuClick, collapsed, onToggleCollapse }: TopBarProps) {
  const { user, logout } = useAuth0();

  return (
    <header className="sticky top-0 z-30 flex h-[60px] shrink-0 items-center justify-between border-b border-border-subtle bg-surface px-4">
      <div className="flex items-center gap-3">
        <button onClick={onMenuClick} className="btn-icon lg:hidden" aria-label="Open navigation">
          <Menu className="h-5 w-5" />
        </button>
        <button
          onClick={onToggleCollapse}
          className="btn-icon hidden lg:inline-flex"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </button>
        <img
          src={logoSvg}
          alt="FirstClose"
          className="h-11 w-auto"
          style={{ filter: 'var(--logo-filter, none)' }}
        />
      </div>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <div className="text-right hidden sm:block">
          <p className="text-sm font-medium text-content">{user?.name || user?.email}</p>
          <p className="text-xs text-content-secondary">{user?.email}</p>
        </div>
        {user?.picture && (
          <img
            src={user.picture}
            alt={user.name || 'User'}
            className="h-8 w-8 rounded-full ring-2 ring-border-subtle"
          />
        )}
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="btn-icon"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
